/**
 * Offline versions of the visit, charting and assessment writes (Phase 16C C5, design §6.7).
 * Each one updates the encrypted repository and queues an op; the sync engine sends it later.
 * Op payloads are the bodies the online endpoints take; offline-only fields (capturedAt,
 * observedAt, timeAnchor) travel on the op, not in the payload (16B B3).
 */
import { ulid } from './ids';
import type { Outbox } from './outbox';
import type { OfflineRuntime } from './runtime';
import type { LocalVisit, VisitCoords, VisitRepo } from './repos/visitRepo';
import type { ChartRepo, LocalChartEntry } from './repos/chartRepo';
import type { AssessmentRepo, LocalAssessment } from './repos/assessmentRepo';
import type { BlobRepo } from './repos/blobRepo';

export interface LocalActionDeps {
  rt: OfflineRuntime;
  visits: VisitRepo;
  charts: ChartRepo;
  assessments: AssessmentRepo;
  blobs: BlobRepo;
  outbox: Outbox;
}

export interface LocalStartInput {
  visitId?: string;
  patientId: string;
  patientName?: string;
  /** The StartVisitPayload the online endpoint takes */
  payload: Record<string, unknown>;
  scheduleId?: string;
  evvRequired?: boolean;
  coords?: VisitCoords | null;
  gpsDenied?: boolean;
}

export interface LocalEndInput {
  signature?: string;
  evvClockOutFlag?: string;
  location?: VisitCoords;
  /** Phase 18 M4: sent with the queued end as typed (the server validates on replay) */
  signerName?: string;
  signerRelationship?: string;
  signatureUnavailableReason?: string;
  noCareReason?: string;
  noCareNote?: string;
}

const END_SIGNOFF_FIELDS = ['signerName', 'signerRelationship', 'signatureUnavailableReason', 'noCareReason', 'noCareNote'] as const;

export interface LocalEntryInput {
  entryType: string;
  data: unknown;
  observedAt?: string;
  amendReason?: string;
}

export class NotAvailableOfflineError extends Error {
  constructor(message = 'This item is not stored on this device. Reconnect to change it.') {
    super(message);
    this.name = 'NotAvailableOfflineError';
  }
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** Server-managed fields never sent in an op payload (16B payloads are strict). */
const SERVER_FIELDS = ['assessmentId', 'entryId', 'visitId', 'status', 'version', 'editVersion', 'reopenHistory', 'createdAt', 'updatedAt'];
export function stripServerFields(body: Record<string, unknown>): Record<string, unknown> {
  const out = { ...body };
  SERVER_FIELDS.forEach((k) => { delete out[k]; });
  return out;
}

export class LocalActions {
  constructor(private readonly d: LocalActionDeps) {}

  private iso(): string { return new Date(this.d.rt.now()).toISOString(); }

  // ---- visits ----

  async startVisit(input: LocalStartInput): Promise<LocalVisit> {
    const visitId = input.visitId || ulid(this.d.rt.now());
    const startedAt = this.iso();
    const p = input.payload;
    const visit = await this.d.visits.startLocal({
      visitId,
      patientId: input.patientId,
      startedAt,
      scheduleId: input.scheduleId,
      coords: input.coords ?? null,
      gpsDenied: input.gpsDenied,
      discipline: str(p.discipline),
      visitType: str(p.visitType),
      serviceType: str(p.serviceType),
      authorizationId: str(p.authorizationId),
      evvRequired: input.evvRequired,
    });
    if (input.patientName) await this.d.visits.put({ ...visit, patientName: input.patientName });
    await this.d.outbox.enqueue({
      type: 'VISIT_START',
      patientId: input.patientId,
      visitId,
      // The online start body only (visitId travels on the op; gpsDenied stays local: location null + locationUnavailable)
      payload: { ...stripServerFields(p), startTime: startedAt },
      shiftId: input.scheduleId,
      capturedAt: startedAt,
    });
    return (await this.d.visits.get(visitId)) as LocalVisit;
  }

  async endVisit(patientId: string, visitId: string, end: LocalEndInput): Promise<LocalVisit> {
    const endedAt = this.iso();
    if (!(await this.d.visits.get(visitId))) {
      // A server visit this device never cached: keep enough to show and sync it
      await this.d.visits.cacheServer({ visitId, patientId, lifecycle: 'IN_PROGRESS', startedAt: '', chartingStatus: 'DRAFT' });
    }
    const signatureBlobId = end.signature ? await this.d.blobs.putDataUrl(end.signature) : undefined;
    const visit = await this.d.visits.endLocal(visitId, endedAt, signatureBlobId);
    await this.d.outbox.enqueue({
      type: 'VISIT_END',
      patientId,
      visitId,
      payload: {
        ...(signatureBlobId ? { signatureBlobId } : {}),
        ...(end.evvClockOutFlag ? { evvClockOutFlag: end.evvClockOutFlag } : {}),
        ...(end.location ? { location: end.location } : {}),
        ...Object.fromEntries(END_SIGNOFF_FIELDS.filter((f) => typeof end[f] === 'string' && end[f]).map((f) => [f, end[f]])),
      },
      capturedAt: endedAt,
    });
    return visit;
  }

  async cancelVisit(patientId: string, visitId: string, payload: Record<string, unknown>): Promise<LocalVisit> {
    if (!(await this.d.visits.get(visitId))) throw new NotAvailableOfflineError();
    const visit = await this.d.visits.cancelLocal(visitId);
    await this.d.outbox.enqueue({ type: 'VISIT_CANCEL', patientId, visitId, payload });
    return visit;
  }

  getActiveVisit(): Promise<LocalVisit | null> {
    return this.d.visits.getActive();
  }

  // ---- charting ----

  async createChartEntry(patientId: string, visitId: string, input: LocalEntryInput, entryId: string = ulid(this.d.rt.now())): Promise<LocalChartEntry> {
    const entry = await this.d.charts.createLocal({ entryId, visitId, patientId, entryType: input.entryType, data: input.data, observedAt: input.observedAt });
    await this.d.outbox.enqueue({
      type: 'CHART_CREATE',
      patientId,
      visitId,
      entityId: entryId,
      payload: { entryType: input.entryType, data: input.data },
      observedAt: input.observedAt,
    });
    return entry;
  }

  async updateChartEntry(patientId: string, visitId: string, entryId: string, input: LocalEntryInput): Promise<LocalChartEntry> {
    const existing = await this.d.charts.get(entryId);
    if (!existing) throw new NotAvailableOfflineError('This entry is not stored on this device. Reconnect to edit it.');
    const entry = await this.d.charts.updateLocal(entryId, { entryType: input.entryType, data: input.data, observedAt: input.observedAt });
    await this.d.outbox.enqueue({
      type: 'CHART_UPDATE',
      patientId,
      visitId,
      entityId: entryId,
      payload: { entryType: input.entryType, data: input.data, ...(input.amendReason ? { amendReason: input.amendReason } : {}) },
      // Always sent: the last version the server confirmed (filled at send time for a local create)
      expectedVersion: existing.syncState === 'synced' ? existing.serverVersion : undefined,
      observedAt: input.observedAt,
    });
    return entry;
  }

  async listChart(visitId: string): Promise<{ entries: LocalChartEntry[]; chartingStatus: string }> {
    const [entries, visit] = await Promise.all([this.d.charts.listForVisit(visitId), this.d.visits.get(visitId)]);
    return { entries, chartingStatus: visit?.chartingStatus || 'DRAFT' };
  }

  async submitChart(patientId: string, visitId: string): Promise<void> {
    const visit = await this.d.visits.get(visitId);
    if (visit) await this.d.visits.put({ ...visit, chartingStatus: 'SUBMITTED', syncState: 'pending' });
    else await this.d.visits.cacheServer({ visitId, patientId, lifecycle: 'ENDED', startedAt: '', chartingStatus: 'SUBMITTED' }).then((v) => this.d.visits.put({ ...v, syncState: 'pending' }));
    await this.d.outbox.enqueue({ type: 'CHART_SUBMIT', patientId, visitId, payload: {} });
  }

  // ---- assessments ----

  async createAssessment(patientId: string, visitId: string, body: Record<string, unknown>, assessmentId: string = ulid(this.d.rt.now())): Promise<LocalAssessment> {
    const a = await this.d.assessments.createLocal(assessmentId, visitId, patientId, { ...body, assessmentId });
    await this.d.outbox.enqueue({ type: 'ASSESSMENT_CREATE', patientId, visitId, entityId: assessmentId, payload: stripServerFields(body) });
    return a;
  }

  async updateAssessment(patientId: string, assessmentId: string, body: Record<string, unknown>): Promise<LocalAssessment> {
    const existing = await this.d.assessments.get(assessmentId);
    if (!existing) throw new NotAvailableOfflineError('This assessment is not stored on this device. Reconnect to edit it.');
    const a = await this.d.assessments.updateLocal(assessmentId, body);
    await this.d.outbox.enqueue({
      type: 'ASSESSMENT_UPDATE',
      patientId,
      visitId: existing.visitId,
      entityId: assessmentId,
      payload: stripServerFields(body),
      expectedVersion: existing.syncState === 'synced' ? existing.serverVersion : undefined,
    });
    return a;
  }

  async completeAssessment(patientId: string, assessmentId: string): Promise<LocalAssessment> {
    const existing = await this.d.assessments.get(assessmentId);
    if (!existing) throw new NotAvailableOfflineError('This assessment is not stored on this device. Reconnect to complete it.');
    const a = await this.d.assessments.completeLocal(assessmentId);
    await this.d.outbox.enqueue({ type: 'ASSESSMENT_COMPLETE', patientId, visitId: existing.visitId, entityId: assessmentId, payload: {} });
    return a;
  }

  listAssessments(visitId: string): Promise<LocalAssessment[]> {
    return this.d.assessments.listForVisit(visitId);
  }
}
